import clsx from "clsx";
import BigNumber from "bignumber.js";
import { useNavigate } from "react-router";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { useEffect, useMemo, useState } from "react";
import useLanguage from "@axvdex/hooks/useLanguage";
import imgSanitize from "@axvdex/utils/imgSanitize";
import styles from "@axvdex/styles/Outbid.module.scss";
import CustomInputButton from "@axvdex/components/form-element/CustomInputButton";
import Button from "@axvdex/components/common/Button";
import { OutbidSale } from "@axvdex/types/solana/outbid";
import SolanaSaleOperatorControls from "@axvdex/components/outbid/solana/SolanaSaleOperatorControls";
import { ReactComponent as IcnTicket } from "@axvdex/assets/icons/icn-outbid-ticket.svg";
import { useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import { useGetBidderInfo } from "@axvdex/hooks/solana/useOutbid";

export type FilterOption = {
  label: any;
  id: "all" | "upcoming" | "live" | "vesting" | "completed";
};

type SolanaSalesSelectGridProps = {
  topFilterOptions: FilterOption[];
  topFilter: "all" | "upcoming" | "live" | "vesting" | "completed";
  setTopFilter: (filter: "all" | "upcoming" | "live" | "vesting" | "completed") => void;
  outbidSales: OutbidSale[];
};

const SolanaSalesSelectGrid = ({
  topFilterOptions,
  topFilter,
  setTopFilter,
  outbidSales,
}: SolanaSalesSelectGridProps) => {
  const { i18 } = useLanguage();
  const navigate = useNavigate();
  const walletInfo = useAppSelector(selectWalletInfo);
  const { connection } = useConnection();
  const [userBidOnOutbid, setUserBidOnOutbid] = useState<Map<string, boolean>>(new Map());

  useEffect(() => {
    const fetchBidderInfo = async () => {
      const userBid = new Map();
      for (const outbidSale of outbidSales) {
        if (!connection || !walletInfo.isConnected) {
          setUserBidOnOutbid(new Map());
          return;
        }
        const bidderInfos = outbidSale.sales.map(async saleAccount => {
          return await useGetBidderInfo(connection, saleAccount, new PublicKey(walletInfo.walletAddress));
        });
        const bidderInfo = await Promise.all(bidderInfos);
        const hasBid = bidderInfo.some(info => info !== null);
        console.log("hasBid", hasBid);
        userBid.set(outbidSale.name, hasBid);
      }
      console.log("userBid", userBid);
      setUserBidOnOutbid(userBid);
    };
    fetchBidderInfo();
  }, [outbidSales, walletInfo]);

  const getOutbidGlobalStatus = (outbid: OutbidSale) => {
    if (!outbid.sales.length) {
      return { text: "Upcoming", id: "upcoming" };
    }
    if (outbid.status === "vesting") {
      return { text: "Vesting", id: "vesting" };
    }
    if (outbid.status === "complete") {
      return { text: "Completed", id: "completed" };
    }
    return { text: "Live", id: "live" };
  };

  return (
    <>
      <div className="salesSectionHeader flexbox withGradientBorderBottom">
        <h1>{i18("Outbid", "sales.title")}</h1>
        <fieldset>
          <legend className="visuallyHidden">{i18("Display", "pools.topFilter.legend")}</legend>
          <div className="btnGroup">
            {topFilterOptions.map(topFilterOption => (
              <CustomInputButton
                key={"input_filter_" + topFilterOption.id}
                type="radio"
                name={topFilterOption.label}
                labelText={topFilterOption.label}
                id={topFilterOption.id}
                value={topFilterOption.id}
                checked={topFilter === topFilterOption.id}
                onChange={() => setTopFilter(topFilterOption.id)}
                onClick={() => setTopFilter(topFilterOption.id)}
              />
            ))}
          </div>
        </fieldset>
      </div>

      <section className="outbidSectionCardGrid gridBox">
        {outbidSales.map(outbidSale => (
          <article
            key={outbidSale.name}
            tabIndex={0}
            className={clsx(styles.outbidCard, "outbidCard")}
            onClick={() => navigate("/outbid/" + outbidSale.name)}
            style={{ cursor: "pointer" }}
          >
            <div className="buttonContainer">
              {outbidSale.saleAuthority === walletInfo.walletAddress /* Add `|| outbidSale.owner` */ && (
                <div
                  onClick={e => {
                    // Block the onClick propagation of the modal
                    e.stopPropagation();
                  }}
                >
                  <SolanaSaleOperatorControls outbidSale={outbidSale} />
                </div>
              )}
              {userBidOnOutbid.has(outbidSale.name) && (
                <Button btnVariant="icon" style={{ margin: "1em" }}>
                  <IcnTicket style={{ fontSize: "2em" }} />
                </Button>
              )}
            </div>

            <div className="outbidCardHeader">
              {outbidSale.projectSymbol && <img src={imgSanitize(outbidSale.projectSymbol)} alt={outbidSale.name} />}
            </div>

            <div style={{ marginTop: ".5em", marginBottom: "1em" }}>
              <h2>{outbidSale.name}</h2>
              <small>{outbidSale.description}</small>
            </div>
            <div style={{ display: "flex", placeContent: "space-between", alignItems: "center" }}>
              <div>
                <span>Status: {getOutbidGlobalStatus(outbidSale).text}</span>
                {outbidSale.status !== "upcoming" && (
                  <>
                    <br />
                    <span>
                      Raised: $
                      {BigNumber(outbidSale.raised)
                        .div(Math.pow(10, 6))
                        .decimalPlaces(2)
                        .toNumber()
                        .toLocaleString("en-US")}
                    </span>
                  </>
                )}
              </div>
              {outbidSale.projectLinks.map(projectLink => (
                <button
                  key={projectLink.url}
                  className={clsx("btn")}
                  title={projectLink.label}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    window.open(projectLink.url, "_blank");
                  }}
                >
                  Website
                </button>
              ))}
            </div>
          </article>
        ))}
      </section>
    </>
  );
};

export default SolanaSalesSelectGrid;
